import { useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import {
  CalendarDays, CheckCircle2, Copy, Loader2, Stamp, X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

// Modal shown when a camper clicks "Check in here" on a campsite detail.
// Two-step flow — matches the mobile app exactly:
//   1) collect check-in date + checkout date + optional remarks
//      → POST /api/checkins/generate → returns a pending check-in doc
//         complete with `checkin_token`
//   2) display the token as a QR + text so the owner (or their staff) can
//      preview and validate it from their mobile.
//
// Backdated stays (checkout_date < today) are supported natively by the
// backend — it applies a 50% EXP penalty and marks the doc `backdated=true`.
export default function CheckinDialog({ campsite, onClose }) {
  const { api } = useAuth();
  const [checkinDate, setCheckinDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [checkoutDate, setCheckoutDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [issued, setIssued] = useState(null);   // the created pending check-in
  const [validated, setValidated] = useState(false);
  const [pollTimer, setPollTimer] = useState(null);

  const isBackdated = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return checkoutDate < today;
  }, [checkoutDate]);

  // Once we have a token, gently poll the check-in every 4s to see if the
  // owner has validated it. Stops when validated OR when the dialog closes.
  useEffect(() => {
    if (!issued?.checkin_token || validated) return;
    const token = issued.checkin_token;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/checkins/by-token/${token}`);
        if (cancelled) return;
        if (data?.status === "validated") {
          setValidated(true);
          toast.success("Stamp collected! Nice work.");
        } else if (data?.status === "rejected") {
          toast.error("The owner rejected this check-in.");
          setIssued({ ...issued, status: "rejected" });
        }
      } catch { /* silent — keep polling */ }
    };
    const t = setInterval(tick, 4000);
    setPollTimer(t);
    return () => { cancelled = true; clearInterval(t); };
  }, [issued, validated, api]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkins/generate", {
        campsite_id: campsite.campsite_id,
        checkin_date: checkinDate,
        checkout_date: checkoutDate,
        remarks: remarks.trim() || null,
      });
      setIssued(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start check-in.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (pollTimer) clearInterval(pollTimer);
    onClose();
  };

  const copyToken = async () => {
    if (!issued?.checkin_token) return;
    try {
      await navigator.clipboard.writeText(issued.checkin_token);
      toast.success("Token copied.");
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
         style={{ background: "rgba(0,0,0,0.55)" }}
         onClick={close}
         data-testid="checkin-dialog">
      <div
        className="card-elev max-w-md w-full max-h-[calc(100vh-3rem)] overflow-y-auto p-6 relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true"
      >
        <button
          onClick={close} className="absolute top-3 right-3 p-2 rounded-lg hover:bg-black/5"
          aria-label="Close" data-testid="checkin-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* -------- Step 1: form ------------------------------------------------ */}
        {!issued && (
          <>
            <h2 className="text-xl font-extrabold mb-1" style={{ color: "var(--on-surface)" }}>
              Check in at {campsite.name}
            </h2>
            <p className="text-sm mb-6" style={{ color: "var(--on-surface-muted)" }}>
              The owner will scan your code to award your stamp.
            </p>
            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="checkin-form">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
                  Check-in
                  <input
                    type="date" required value={checkinDate}
                    onChange={(e) => {
                      const v = e.target.value; setCheckinDate(v);
                      if (checkoutDate < v) setCheckoutDate(v);
                    }}
                    className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm font-normal normal-case tracking-normal"
                    style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                    data-testid="checkin-date-in"
                  />
                </label>
                <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
                  Check-out
                  <input
                    type="date" required value={checkoutDate}
                    min={checkinDate}
                    onChange={(e) => setCheckoutDate(e.target.value)}
                    className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm font-normal normal-case tracking-normal"
                    style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                    data-testid="checkin-date-out"
                  />
                </label>
              </div>
              {isBackdated && (
                <div className="text-xs p-3 rounded-xl flex items-start gap-2"
                     style={{ background: "color-mix(in srgb, var(--premium) 12%, transparent)", color: "var(--premium)" }}
                     data-testid="checkin-backdated-hint">
                  <CalendarDays className="w-4 h-4 mt-0.5 shrink-0" />
                  Backdated stamp — XP is halved for past stays.
                </div>
              )}
              <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
                Remarks (optional)
                <textarea
                  value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} maxLength={280}
                  placeholder="Anything the owner should know…"
                  className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm resize-none font-normal normal-case tracking-normal"
                  style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                  data-testid="checkin-remarks"
                />
              </label>
              <button type="submit" disabled={loading} className="btn btn-green justify-center w-full mt-2"
                      data-testid="checkin-submit">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Stamp className="w-4 h-4" /> Generate my code</>}
              </button>
            </form>
          </>
        )}

        {/* -------- Step 2: token / QR ---------------------------------------- */}
        {issued && !validated && issued.status !== "rejected" && (
          <div className="flex flex-col items-center text-center gap-4" data-testid="checkin-token-view">
            <div>
              <h2 className="text-xl font-extrabold" style={{ color: "var(--on-surface)" }}>Show this to the owner</h2>
              <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
                They'll scan the code or type in the token.
              </p>
            </div>
            <div className="p-4 rounded-2xl" style={{ background: "white" }}>
              <QRCode
                value={issued.checkin_token}
                size={196}
                data-testid="checkin-qr"
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
            <div className="w-full">
              <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1"
                   style={{ color: "var(--on-surface-muted)" }}>
                Token
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 rounded-lg font-mono text-xs break-all text-left"
                     style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)", color: "var(--on-surface)" }}
                     data-testid="checkin-token-text">
                  {issued.checkin_token}
                </div>
                <button onClick={copyToken} className="btn btn-light" title="Copy" data-testid="checkin-copy-token">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="text-xs flex items-center gap-2" style={{ color: "var(--on-surface-muted)" }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Waiting for the owner to validate…
            </div>
            <button onClick={close} className="btn btn-light w-full justify-center" data-testid="checkin-token-close">
              Close
            </button>
          </div>
        )}

        {/* -------- Success --------------------------------------------------- */}
        {validated && (
          <div className="flex flex-col items-center text-center gap-4 py-4" data-testid="checkin-success">
            <div className="w-20 h-20 rounded-full flex items-center justify-center"
                 style={{ background: "color-mix(in srgb, var(--brand) 15%, transparent)" }}>
              <CheckCircle2 className="w-10 h-10" style={{ color: "var(--brand)" }} />
            </div>
            <div>
              <h2 className="text-xl font-extrabold" style={{ color: "var(--on-surface)" }}>Stamp collected!</h2>
              <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
                Nice work — it's on your passport now.
              </p>
            </div>
            <button onClick={close} className="btn btn-green w-full justify-center" data-testid="checkin-success-close">
              Great, back to campsite
            </button>
          </div>
        )}

        {/* -------- Rejected -------------------------------------------------- */}
        {issued?.status === "rejected" && (
          <div className="flex flex-col items-center text-center gap-4 py-4" data-testid="checkin-rejected">
            <div className="w-20 h-20 rounded-full flex items-center justify-center"
                 style={{ background: "color-mix(in srgb, var(--premium) 15%, transparent)" }}>
              <X className="w-10 h-10" style={{ color: "var(--premium)" }} />
            </div>
            <div>
              <h2 className="text-xl font-extrabold" style={{ color: "var(--on-surface)" }}>Check-in rejected</h2>
              <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
                The owner rejected this stay. Reach out to them if you think this was a mistake.
              </p>
            </div>
            <button onClick={close} className="btn btn-light w-full justify-center">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
